import { useState, useEffect, useRef, useCallback } from 'react';
import type { Prompt, FileSystem, User, UserData } from '../../types';
import { loadUserData, saveUserData, fetchUserData, fetchUserDataVersion, saveUserDataToServer } from '../../services/storageService';
import { cleanupTrash } from '../../utils/fileSystemUtils.ts';
import { parseServerTimestamp } from '../../utils/timestamps';
import { mergeFileSystems, mergeTemplates } from '../../utils/syncMerge';

type SyncStatus = 'idle' | 'saving' | 'offline' | 'unauthorized' | 'error';

type ShowToast = (message: string, type?: 'success' | 'error') => void;

// Wie oft ein sichtbarer Tab nachsieht, ob der Server neuere Daten hat (z. B. vom Skill-Sync)
const SERVER_VERSION_POLL_MS = 30_000;

const useUserData = (
  currentUser: User | null,
  showToast: ShowToast,
  onDataLoaded?: () => void,
) => {
  const [fileSystem, setFileSystem] = useState<FileSystem>([]);
  const [templates, setTemplates] = useState<Prompt[]>([]);
  const [settings, setSettings] = useState<UserData['settings']>({ 
    globalSystemInstruction: '',
    promptGeneratorSystemPrompt: '',
    promptOptimizerSystemPrompt: ''
  });
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const onDataLoadedRef = useRef(onDataLoaded);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dataLoadedRef = useRef(false);
  const loadedUserIdRef = useRef<number | null>(null);
  const serverUpdatedAtRef = useRef<string | undefined>(undefined);
  const settingsRef = useRef(settings);
  const fileSystemRef = useRef(fileSystem);
  const templatesRef = useRef(templates);
  const savingRef = useRef(false);
  const skipServerSaveRef = useRef(false);
  const showToastRef = useRef(showToast);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    fileSystemRef.current = fileSystem;
    templatesRef.current = templates;
  }, [fileSystem, templates]);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  // Nach 409: Serverstand holen und mit dem lokalen zusammenführen; der nächste Speicherlauf schreibt das Ergebnis
  const resolveConflict = useCallback(async (userId: number) => {
    const serverData = await fetchUserData(userId);
    if (!serverData || loadedUserIdRef.current !== userId) return;
    serverUpdatedAtRef.current = serverData.updatedAt;
    setFileSystem(cleanupTrash(mergeFileSystems(fileSystemRef.current, serverData.fileSystem)));
    setTemplates(mergeTemplates(templatesRef.current, serverData.templates));
    showToastRef.current('Bibliothek wurde an anderer Stelle geändert und zusammengeführt.');
  }, []);

  const pushToServer = useCallback(async (userId: number, data: UserData) => {
    savingRef.current = true;
    try {
      const res = await saveUserDataToServer(userId, { ...data, lastKnownUpdatedAt: serverUpdatedAtRef.current }, setSyncStatus);
      if (res.updatedAt) serverUpdatedAtRef.current = res.updatedAt;
      if (res.conflict) await resolveConflict(userId);
      return res.success;
    } finally {
      savingRef.current = false;
    }
  }, [resolveConflict]);

  useEffect(() => {
    onDataLoadedRef.current = onDataLoaded;
  }, [onDataLoaded]);

  useEffect(() => {
    dataLoadedRef.current = false;
    serverUpdatedAtRef.current = undefined;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    if (!currentUser) {
      loadedUserIdRef.current = null;
      setFileSystem([]);
      setTemplates([]);
      return;
    }

    const currentUserId = currentUser.id;
    loadedUserIdRef.current = currentUserId;

    const loadData = async () => {
      // 1. Initial Load: Vorrangig lokale Daten laden für sofortige Anzeige
      const localData = loadUserData(currentUser.email);
      if (loadedUserIdRef.current !== currentUserId) return;
      setFileSystem(cleanupTrash(localData.fileSystem));
      setTemplates(localData.templates);
      if (localData.settings) setSettings(localData.settings);
      onDataLoadedRef.current?.();

      // 2. Sync: Daten vom Server holen und vergleichen
      const serverData = await fetchUserData(currentUser.id);
      if (loadedUserIdRef.current !== currentUserId) return;
      if (serverData) {
        serverUpdatedAtRef.current = serverData.updatedAt;
        const localTs = parseServerTimestamp(localData.updatedAt);
        // Ältere Server liefern SQLite-DATETIME ohne Zone; parseServerTimestamp liest das als UTC
        const serverTs = parseServerTimestamp(serverData.updatedAt);

        const serverHasEmptyFs = Array.isArray(serverData.fileSystem) && serverData.fileSystem.length === 0;
        const localHasInitialFs = Array.isArray(localData.fileSystem) && localData.fileSystem.length > 0 && (!localData.updatedAt || localTs === 0);

        if (serverHasEmptyFs && localHasInitialFs) {
          // Server hat leeres Dateisystem für Erstnutzer; Initial-Daten erhalten und auf Server persistieren
          pushToServer(currentUser.id, localData);
        } else if (serverTs >= localTs) {
          // Server ist aktueller oder gleichauf -> Server-Daten übernehmen
          setFileSystem(cleanupTrash(serverData.fileSystem));
          setTemplates(serverData.templates);
          if (serverData.settings) setSettings(serverData.settings);
        } else {
          pushToServer(currentUser.id, localData);
        }
      }
      if (loadedUserIdRef.current === currentUserId) {
        dataLoadedRef.current = true;
      }
    };
    loadData();
  }, [currentUser, pushToServer]);

  useEffect(() => {
    if (!currentUser || !dataLoadedRef.current || loadedUserIdRef.current !== currentUser.id) return;
    saveUserData(currentUser.email, fileSystem, templates, settings);
    if (skipServerSaveRef.current) {
      // Stand kam gerade vom Server, nichts zurückzuschreiben
      skipServerSaveRef.current = false;
      return;
    }
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      saveTimeoutRef.current = null;
      if (loadedUserIdRef.current !== currentUser.id) return;
      await pushToServer(currentUser.id, { fileSystem, templates, settings: settingsRef.current });
    }, 1000);
  }, [fileSystem, templates, settings, currentUser, pushToServer]);

  // Serverseitige Änderungen (Skill-Sync, anderer Tab) übernehmen, solange lokal nichts ungespeichert ist
  useEffect(() => {
    if (!currentUser) return;
    const userId = currentUser.id;
    const isIdle = () => dataLoadedRef.current && loadedUserIdRef.current === userId && !saveTimeoutRef.current && !savingRef.current;

    const checkServer = async () => {
      if (document.visibilityState !== 'visible' || !isIdle()) return;
      const version = await fetchUserDataVersion(userId);
      if (!version?.updatedAt || version.updatedAt === serverUpdatedAtRef.current || !isIdle()) return;
      const serverData = await fetchUserData(userId);
      if (!serverData || !isIdle()) return;
      serverUpdatedAtRef.current = serverData.updatedAt;
      skipServerSaveRef.current = true;
      setFileSystem(cleanupTrash(serverData.fileSystem));
      setTemplates(serverData.templates);
      if (serverData.settings) setSettings(serverData.settings);
    };

    const interval = setInterval(checkServer, SERVER_VERSION_POLL_MS);
    document.addEventListener('visibilitychange', checkServer);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', checkServer);
    };
  }, [currentUser]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (syncStatus === 'offline') {
      showToast('Offline: Änderungen werden lokal gespeichert.', 'error');
    } else if (syncStatus === 'unauthorized') {
      showToast('Sitzung abgelaufen. Bitte erneut anmelden.', 'error');
    } else if (syncStatus === 'error') {
      showToast('Serverfehler beim Speichern der Daten.', 'error');
    }
  }, [syncStatus, showToast]);

  const forceImmediateSync = useCallback(async (
    nextFileSystem?: FileSystem,
    nextTemplates?: Prompt[],
  ): Promise<boolean> => {
    if (!currentUser) return false;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const fsToSave = nextFileSystem ?? fileSystem;
    const templatesToSave = nextTemplates ?? templates;
    const settingsToSave = settingsRef.current ?? settings;
    return pushToServer(currentUser.id, { fileSystem: fsToSave, templates: templatesToSave, settings: settingsToSave });
  }, [currentUser, fileSystem, templates, settings, pushToServer]);

  const refreshData = useCallback(async () => {
    if (!currentUser) return;
    const serverData = await fetchUserData(currentUser.id);
    if (serverData) {
      setFileSystem(cleanupTrash(serverData.fileSystem));
      setTemplates(serverData.templates);
      if (serverData.settings) setSettings(serverData.settings);
      saveUserData(currentUser.email, serverData.fileSystem, serverData.templates, serverData.settings || settings);
    }
  }, [currentUser, settings]);

  // Support both direct values and updater functions for setSettings
  const updateSettings = useCallback((settingsOrUpdater: UserData['settings'] | ((prev: UserData['settings']) => UserData['settings'])) => {
    if (typeof settingsOrUpdater === 'function') {
      setSettings(prev => settingsOrUpdater(prev));
    } else {
      setSettings(settingsOrUpdater);
    }
  }, []);

  return { fileSystem, setFileSystem, templates, setTemplates, settings, setSettings: updateSettings, syncStatus, forceImmediateSync, refreshData };
};

export default useUserData;
